// Author: Ahsan Saleem
/**
 * First-party BloomOS glyphs — the operating environment's own iconography for
 * its applications. Coherent 24-grid line art (currentColor stroke) so they sit
 * beside Lucide's action icons without clashing. Distinctive, not decorative.
 */
interface GlyphProps { size?: number; className?: string; }
const svg = (size: number, className: string, children: React.ReactNode) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden focusable="false">
    {children}
  </svg>
);

/** Pulse — a facility heartbeat resolving into a bloom node. */
export const GlyphPulse = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><path d="M2 12h4l2.2-5.5L11 17l2.4-6 1.4 2.5H18" /><circle cx="20" cy="12" r="1.6" fill="currentColor" stroke="none" /></>);

/** Grow — a seedling rising through stages. */
export const GlyphGrow = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><path d="M12 21v-8" /><path d="M12 13c-3.2 0-5-1.6-5-4.4 2.9-.2 5 1 5 3.4" /><path d="M12 11c0-2.8 1.7-4.6 4.6-4.6.1 2.7-1.6 4.6-4.6 4.6" /><path d="M8 21h8" /></>);

/** Facility — the four-zone grow room. */
export const GlyphFacility = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><rect x="3.5" y="3.5" width="17" height="17" rx="2.5" /><path d="M12 4v16M4 12h16" /><circle cx="8" cy="8" r="1" fill="currentColor" stroke="none" /></>);

/** Simulation — a flask with a modelled wave and a dashed (not-live) base. */
export const GlyphSim = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><path d="M9 3v5.5L4.6 17a2 2 0 0 0 1.8 3h11.2a2 2 0 0 0 1.8-3L15 8.5V3" /><path d="M8 3h8" /><path d="M6.4 14c1.6-1.1 3.6-1.1 5.2 0s3.6 1.1 5.2 0" /></>);

/** System — a core node held by three orbits. */
export const GlyphSystem = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><circle cx="12" cy="12" r="2.3" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="8" opacity=".9" /><path d="M12 4v3M18.9 8 16.3 9.5M18.9 16 16.3 14.5M12 20v-3M5.1 16 7.7 14.5M5.1 8 7.7 9.5" /></>);

/** Alerts — an exception mark that is not a plain triangle. */
export const GlyphAlerts = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><path d="M12 3.5 3.6 18a1.6 1.6 0 0 0 1.4 2.4h14a1.6 1.6 0 0 0 1.4-2.4L12 3.5Z" /><path d="M12 9.5v4" /><circle cx="12" cy="16.6" r="0.5" fill="currentColor" stroke="none" /></>);

/** Today — a queue of work with a completion tick. */
export const GlyphToday = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><path d="M4 6h10M4 12h10M4 18h6" /><path d="M17 15.5l2 2 3-3.5" /></>);

/** Record — capturing an observation. */
export const GlyphRecord = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><path d="M4 20l1-4L16.5 4.5a2.1 2.1 0 0 1 3 3L8 19l-4 1Z" /><path d="M14.5 6.5l3 3" /></>);

/** Reports — trends over time. */
export const GlyphReports = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><path d="M4 4v16h16" /><path d="M7 15l3.5-4 3 2.5L20 8" /></>);

/** Maintenance — a service wrench crossed with a calibration tick. */
export const GlyphMaintenance = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><path d="M15.5 4.5a4 4 0 0 0-5 5L4 16v3.5H7.5L14 13a4 4 0 0 0 5-5l-2.6 2.6-2.4-.6-.6-2.4L15.5 4.5Z" /></>);

/** Edge — a device node with I/O pins reaching outward. */
export const GlyphEdge = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><rect x="7.5" y="7.5" width="9" height="9" rx="1.5" /><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" /><path d="M10 7.5V4.5M14 7.5V4.5M10 19.5v-3M14 19.5v-3M7.5 10h-3M7.5 14h-3M19.5 10h-3M19.5 14h-3" /></>);

/** Commerce — a ledger page with a value line (supplier ↔ customer truth). */
export const GlyphCommerce = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><rect x="4.5" y="3.5" width="15" height="17" rx="2" /><path d="M8 8h5M8 12h8M8 16h6" /><circle cx="16" cy="8" r="1" fill="currentColor" stroke="none" /></>);

/** Catalog — a labelled item box. */
export const GlyphCatalog = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><path d="M4 8l8-4 8 4-8 4-8-4Z" /><path d="M4 8v8l8 4 8-4V8" /><path d="M12 12v8" /></>);

/** Stock — warehouse shelving. */
export const GlyphStock = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><rect x="3.5" y="4.5" width="17" height="15" rx="1.5" /><path d="M3.5 9.5h17M3.5 14.5h17M9 4.5v5M14 9.5v5M9 14.5v5" /></>);

/** Supply — a purchase document with an approval tick. */
export const GlyphSupply = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><path d="M6 3.5h9l4 4v13H6z" /><path d="M14.5 3.5v4h4" /><path d="M9 12l1.6 1.6L14 10" /></>);

/** Receiving — a box with an inbound arrow. */
export const GlyphReceiving = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><path d="M4 10l8-4 8 4-8 4-8-4Z" /><path d="M4 10v6l8 4 8-4v-6" /><path d="M12 3v6M9.5 6.5L12 9l2.5-2.5" /></>);

/** Billing — an invoice with a three-way match tick. */
export const GlyphBilling = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><path d="M6 3h12v18l-3-1.5L12 21l-3-1.5L6 21z" /><path d="M9 8h6M9 12h4" /><path d="M13.5 15.5l1.4 1.4L18 14" /></>);

/** Trace — a genealogy fork: one lot branching into its descendants. */
export const GlyphTrace = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><circle cx="6" cy="12" r="2" /><circle cx="18" cy="6" r="2" /><circle cx="18" cy="18" r="2" /><path d="M8 12h4M12 12l4-4.5M12 12l4 4.5" /></>);

/** Sales — an outbound order tagged with a value mark. */
export const GlyphSales = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><path d="M4 5h2l1.5 10.5a1.6 1.6 0 0 0 1.6 1.4H18" /><path d="M7 8h12l-1.3 6.2H8.2" /><circle cx="9.5" cy="20" r="1.1" fill="currentColor" stroke="none" /><circle cx="17" cy="20" r="1.1" fill="currentColor" stroke="none" /></>);

/** Tax — a stamped statutory document with a seal. */
export const GlyphTax = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><path d="M6 3h8l4 4v10.5a1.5 1.5 0 0 1-1.5 1.5H6z" /><path d="M14 3v4h4" /><circle cx="12" cy="13.5" r="2.6" /><path d="M12 16.1v2.4" /></>);

/** Accounting — a bridge linking two ledgers across a reconciliation gap. */
export const GlyphAccounting = ({ size = 20, className = '' }: GlyphProps) =>
  svg(size, className, <><rect x="3" y="4" width="6" height="6" rx="1" /><rect x="15" y="4" width="6" height="6" rx="1" /><path d="M6 10v5a3 3 0 0 0 3 3h6a3 3 0 0 0 3-3v-5" /><path d="M12 13v6M9.5 16.5l2.5 2.5 2.5-2.5" /></>);

const GLYPHS: Record<string, (p: GlyphProps) => React.ReactElement> = {
  pulse: GlyphPulse, grow: GlyphGrow, facility: GlyphFacility, sim: GlyphSim,
  system: GlyphSystem, alerts: GlyphAlerts, today: GlyphToday, record: GlyphRecord, reports: GlyphReports,
  maintenance: GlyphMaintenance, edge: GlyphEdge, commerce: GlyphCommerce, catalog: GlyphCatalog, stock: GlyphStock, supply: GlyphSupply, receiving: GlyphReceiving, billing: GlyphBilling, trace: GlyphTrace, sales: GlyphSales, tax: GlyphTax, accounting: GlyphAccounting,
};
export function Glyph({ name, size = 20, className = '' }: { name: string } & GlyphProps) {
  const G = GLYPHS[name] ?? GlyphSystem;
  return <G size={size} className={className} />;
}
